import { FC, useCallback, useMemo, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Table } from 'core-components/library/list'
import type { ICockpitExportView } from 'partner360/features/financialCockpit/api'
import {
  formatCockpitMoney,
  getExpectedExitQuarters,
  getScoredHierarchyRows,
  LAST_FQUARTER,
  TRevenueAndSobHierarchyOrder,
} from 'partner360/features/financialCockpit/lib'
import { useFinSobHierarchyBreakdown } from 'partner360/features/financialCockpit/model/hooks'
import { useCockpitSettings } from 'partner360/features/financialCockpit/model/hooks/useCockpitSettings'
import {
  type TCockpitExportSlot,
  useFillCockpitExportSlot,
} from 'partner360/features/financialCockpit/ui/components/cockpitExport'
import { CockpitHierarchyOrder } from 'partner360/features/financialCockpit/ui/components/cockpitHierarchyOrder'
import {
  getHierarchyExportTable,
  getPopulatedHierarchyRows,
  useCockpitPartnerLinkParams,
  useGeoShortNameById,
  useHierarchyExpansion,
  useOfferingNameByCode,
  usePartnerNameById,
} from 'partner360/features/financialCockpit/ui/components/cockpitHierarchyTable'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'

import { getRevenueAndSobTableColumns } from './helpers/getRevenueAndSobTableColumns'

import styles from './CockpitRevenueAndSobTable.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.revenueAndSob

/** Names the hierarchy-order switch in the export; on screen the two buttons speak for
 *  themselves. The same label the Initiatives table uses, so both decks read alike. */
const HIERARCHY_LABEL = 'Hierarchy'

interface ICockpitRevenueAndSobTableProps {
  /** Whether the Geo axis (and its Geo>Product/Product>Geo switcher) is shown at all — manager
   *  cockpit mode only, mirroring `CockpitKeyInitiativesExpanded`'s own `showGeoHierarchy` prop. A
   *  single partner has one geo, so a geo breakdown isn't meaningful in partner mode. */
  showGeoHierarchy: boolean
  exportSlot?: TCockpitExportSlot
}

export const CockpitRevenueAndSobTable: FC<ICockpitRevenueAndSobTableProps> = ({
  showGeoHierarchy,
  exportSlot,
}) => {
  const [userHierarchyOrder, setHierarchyOrder] =
    useState<TRevenueAndSobHierarchyOrder>('geoProduct')
  /** Pinning to `'productGeo'` when the Geo axis is disabled makes every (product-only) edge
   *  classify as primary in `useHierarchyExpansion`, since no row is ever Geo-shaped in that case. */
  const hierarchyOrder = showGeoHierarchy ? userHierarchyOrder : 'productGeo'

  const {
    selectedFYear: fYear,
    currency,
    showQuota,
    isPeriodOpen,
    isPrevFYearClosing,
    periods,
    currentFQuarter,
  } = useCockpitSettings()
  const { rows: allHierarchyRows } = useFinSobHierarchyBreakdown(showGeoHierarchy, hierarchyOrder)

  /** Which quarters the expected exit reads as actuals and which as plan — what the Expected Exit
   *  headers' tooltips list. */
  const expectedExitQuarters = useMemo(
    () => getExpectedExitQuarters(periods, currentFQuarter),
    [periods, currentFQuarter]
  )

  /** Only while the year before the selected one is still in its last quarter: which of its
   *  quarters that year's expected exit reads as actuals and which as plan — Q1–Q3 and Q4. */
  const prevFYearExpectedExitQuarters = useMemo(
    () => (isPrevFYearClosing ? getExpectedExitQuarters(periods, LAST_FQUARTER) : undefined),
    [isPrevFYearClosing, periods]
  )

  /** Nodes with nothing behind any on-screen column are pruned here rather than in the builder:
   *  whether the Quota column is shown is a cockpit setting `getRevenueAndSobHierarchyRows` never
   *  sees, and the row set itself stays honest about what the backend returned. */
  const hierarchyRows = useMemo(
    () => getPopulatedHierarchyRows(allHierarchyRows, showQuota),
    [allHierarchyRows, showQuota]
  )

  /** Scored unconditionally rather than only for a period with actuals: it is a cheap map, and one
   *  row shape keeps the columns builder free of a second generic. For an open period every score
   *  comes out `null` — the same condition under which the SCORE columns aren't built at all. */
  const scoredRows = useMemo(() => getScoredHierarchyRows(hierarchyRows), [hierarchyRows])

  const geoShortNameById = useGeoShortNameById()
  const offeringNameByCode = useOfferingNameByCode()
  const partnerNameById = usePartnerNameById()
  const partnerLinkParams = useCockpitPartnerLinkParams()
  /** A single partner's table opens with every Offering showing its Categories, each time it is
   *  shown — closing the tile unmounts it, so reopening starts here again. The manager table still
   *  opens at Total alone. */
  const { visibleRows, controls } = useHierarchyExpansion(scoredRows, hierarchyOrder, {
    expandToLevel: showGeoHierarchy ? undefined : 'category',
  })
  const translate = useSafetyTranslation()

  const formatMoney = useCallback(
    (value: number | null) => formatCockpitMoney(value, currency),
    [currency]
  )

  const columns = useMemo(
    () =>
      getRevenueAndSobTableColumns({
        fYear,
        formatMoney,
        showQuota,
        isPeriodOpen,
        expectedExitQuarters,
        prevFYearExpectedExitQuarters,
        showGeoHierarchy,
        hierarchyOrder,
        controls,
        geoShortNameById,
        offeringNameByCode,
        partnerNameById,
        partnerLinkParams,
        translate,
      }),
    [
      fYear,
      formatMoney,
      showQuota,
      isPeriodOpen,
      expectedExitQuarters,
      prevFYearExpectedExitQuarters,
      showGeoHierarchy,
      hierarchyOrder,
      controls,
      geoShortNameById,
      offeringNameByCode,
      partnerNameById,
      partnerLinkParams,
      translate,
    ]
  )

  /** The table as it stands: the rows on screen, opened as far as the user opened them, read off
   *  the very columns that draw it. The tile adds the strip that says which view this is. */
  const exportView = useCallback(
    (): ICockpitExportView => ({
      key: 'finSob',
      controls: showGeoHierarchy
        ? [
            {
              label: HIERARCHY_LABEL,
              value: translate(
                hierarchyOrder === 'geoProduct'
                  ? KEYS.columnOrderGeoProduct
                  : KEYS.columnOrderProductGeo
              ),
              options: [
                translate(KEYS.columnOrderProductGeo),
                translate(KEYS.columnOrderGeoProduct),
              ],
            },
          ]
        : [],
      table: getHierarchyExportTable({ columns, rows: visibleRows, controls, translate }),
    }),
    [showGeoHierarchy, hierarchyOrder, columns, visibleRows, controls, translate]
  )

  useFillCockpitExportSlot(exportSlot, exportView)

  return (
    <div className={styles.root}>
      {showGeoHierarchy && (
        <CockpitHierarchyOrder selectedOrder={hierarchyOrder} onSelectOrder={setHierarchyOrder} />
      )}

      <Table
        items={visibleRows}
        classes={{ body: styles.body }}
        columns={columns}
        keyField="rowId"
        pageSize={visibleRows.length}
        header
        className={styles.table}
        placeholderContent={() => <Translation text={KEYS.noRowsAtLevel} span />}
      />
    </div>
  )
}

CockpitRevenueAndSobTable.displayName = 'CockpitRevenueAndSobTable'
